import { useQuery } from "@tanstack/react-query";
import { BundleAPIPromiseClient } from "gen/witbooking/backoffice/bundle/v1/bundle_api_grpc_web_pb";
import {
  Bundle,
  BundleType,
  GetBundlesRequest,
} from "gen/witbooking/backoffice/bundle/v1/bundle_api_pb";
import { useEffect, useMemo, useState } from "react";
import { useLocaleProvider } from "../../components/locale-provider";
import { useProperty } from "../../components/property-provider";
import { AriAPIPromiseClient } from "../../gen/witbooking/ari/v1/ari_api_grpc_web_pb";
import {
  ListBundleStaticDataRequest,
  ListComplementStaticDataRequest,
  ListDiscountStaticDataRequest,
  ListExtraStaticDataRequest,
} from "../../gen/witbooking/ari/v1/ari_api_pb";
import {
  StaticBundleData,
  StaticComplementData,
  StaticDiscountData,
  StaticExtraData,
} from "../../gen/witbooking/ari/v1/static_ari_pb";
import { InventoryAPIPromiseClient } from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_grpc_web_pb";
import {
  ConstantSharedOwnUnionValue,
  GetInventoryLinesRequest,
  InventoryLine,
  RateValue,
  SharedOwnUnionValue,
} from "../../gen/witbooking/backoffice/inventory/v1/inventory_api_pb";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { useCurrentInventoryStateQuery } from "../inventory/inventory.hook";

export type StaticBundleDataWithName = StaticBundleData.AsObject & {
  name: string;
};

export const useInventoryLines = (): InventoryLine.AsObject[] | undefined => {
  const grpcClient = useGrpcClient(InventoryAPIPromiseClient);
  const locale = useLocaleProvider();
  const property = useProperty();
  const getInventoryLines = usePropertyGrpcCall(grpcClient, grpcClient.getInventoryLines);

  const { data } = useQuery({
    queryKey: ["inventoryLines", property.ticker, locale?.currentLocale],
    queryFn: async () => {
      const inventoryLinesRequest = new GetInventoryLinesRequest();
      inventoryLinesRequest.setOnlyValidLines(false);
      inventoryLinesRequest.setLocale(locale?.currentLocale?.substring(0, 2));
      const getInventoryLinesResponse = await getInventoryLines(inventoryLinesRequest);
      return getInventoryLinesResponse.getInventoryLinesList()?.map(m => m.toObject());
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: (_, error) => {
      console.error("Inventory lines error:", error);
      return false;
    },
  });

  return data;
};

export const useBundles = (): StaticBundleDataWithName[] | undefined => {
  const [result, setResult] = useState<StaticBundleDataWithName[] | undefined>(undefined);
  const locale = useLocaleProvider();
  const ariGrpcClient = useGrpcClient(AriAPIPromiseClient);
  const listBundleStaticData = usePropertyGrpcCall(
    ariGrpcClient,
    ariGrpcClient.listBundleStaticData
  );

  const bundleGrpcClient = useGrpcClient(BundleAPIPromiseClient);
  const getBundles = usePropertyGrpcCall(bundleGrpcClient, bundleGrpcClient.getBundles);

  useEffect(() => {
    const fetchData = async () => {
      const bundlesRequest = new GetBundlesRequest();
      bundlesRequest.setType(BundleType.ENGINE);
      const bundlesResponse = await getBundles(bundlesRequest);
      const bundles = bundlesResponse.getBundleList().reduce(
        (acc, bundle) => ({
          ...acc,
          [bundle.getTicker()]: bundle.toObject(),
        }),
        {} as Record<string, Bundle.AsObject>
      );

      const listBundleStaticDataRequest = new ListBundleStaticDataRequest();
      const listBundleStaticDataResponse = await listBundleStaticData(listBundleStaticDataRequest);
      return listBundleStaticDataResponse
        .toObject()
        .bundleDataMap.flatMap(it => it[1])
        .map(staticBundle => {
          const bundle: Bundle.AsObject | undefined = bundles[staticBundle.ticker];
          const translation =
            bundle?.translationsList.find(it => it.locale === locale.currentLocale) ??
            bundle?.translationsList[0];

          return {
            ...staticBundle,
            name: translation?.name ?? staticBundle.ticker,
          } as StaticBundleDataWithName;
        });
    };

    fetchData()
      .then(res => setResult(res))
      .catch(err => {
        setResult([]);
        console.error("Bundles error:", err);
      });
  }, [ariGrpcClient, getBundles, listBundleStaticData, locale.currentLocale]);

  return result;
};

interface InventoryState {
  [key: string]: {
    parentTicker?: string;
    ticker: string;
    own?: string;
    constant?: number;
    formula?: string;
  };
}

export const useCurrentInventoryState = (): InventoryState[] | undefined => {
  const property = useProperty();
  const { data } = useCurrentInventoryStateQuery(property.ticker);

  return useMemo(
    () =>
      data?.map(it =>
        Object.entries(it.toObject())
          .filter(([valueType, value]) => {
            const castedValue = value as
              | SharedOwnUnionValue.AsObject
              | RateValue.AsObject
              | ConstantSharedOwnUnionValue.AsObject;
            return (
              valueType === "ticker" ||
              castedValue?.parentTicker ||
              castedValue?.own === "Propia" ||
              (castedValue as RateValue.AsObject)?.formula ||
              (castedValue as RateValue.AsObject)?.parametrizedFormula
            );
          })
          .reduce(
            (acc, [valueType, value]) => ({
              [valueType === "lock" ? "closed" : valueType]: value,
              ...acc,
            }),
            {}
          )
      ),
    [data]
  );
};

export const useDiscountStaticData = (): StaticDiscountData.AsObject[] | undefined => {
  const [result, setResult] = useState<StaticDiscountData.AsObject[] | undefined>(undefined);
  const grpcClient = useGrpcClient(AriAPIPromiseClient);
  const listDiscountStaticData = usePropertyGrpcCall(grpcClient, grpcClient.listDiscountStaticData);

  useEffect(() => {
    const fetchData = async () => {
      const listDiscountStaticDataRequest = new ListDiscountStaticDataRequest();
      const listDiscountStaticDataResponse = await listDiscountStaticData(
        listDiscountStaticDataRequest
      );
      return listDiscountStaticDataResponse?.toObject()?.discountDataMap?.flatMap(it => it[1]);
    };

    fetchData()
      .then(res => setResult(res))
      .catch(err => {
        setResult([]);
        console.error("Discount static data error:", err);
      });
  }, [grpcClient, listDiscountStaticData]);

  return result;
};

export const useExtraStaticData = (): StaticExtraData.AsObject[] | undefined => {
  const [result, setResult] = useState<StaticExtraData.AsObject[] | undefined>(undefined);
  const grpcClient = useGrpcClient(AriAPIPromiseClient);
  const listExtraStaticData = usePropertyGrpcCall(grpcClient, grpcClient.listExtraStaticData);

  useEffect(() => {
    const fetchData = async () => {
      const listExtraStaticDataRequest = new ListExtraStaticDataRequest();
      const listExtraStaticDataResponse = await listExtraStaticData(listExtraStaticDataRequest);
      return listExtraStaticDataResponse?.toObject()?.extraDataMap?.flatMap(it => it[1]);
    };

    fetchData()
      .then(res => setResult(res))
      .catch(err => {
        setResult([]);
        console.error("Extra static data error:", err);
      });
  }, [grpcClient, listExtraStaticData]);

  return result;
};

export const useComplementStaticData = (): StaticComplementData.AsObject[] | undefined => {
  const [result, setResult] = useState<StaticComplementData.AsObject[] | undefined>(undefined);
  const grpcClient = useGrpcClient(AriAPIPromiseClient);
  const listComplementStaticData = usePropertyGrpcCall(
    grpcClient,
    grpcClient.listComplementStaticData
  );

  useEffect(() => {
    const fetchData = async () => {
      const listComplementStaticDataRequest = new ListComplementStaticDataRequest();
      const listComplementStaticDataResponse = await listComplementStaticData(
        listComplementStaticDataRequest
      );
      return listComplementStaticDataResponse?.toObject()?.complementDataMap?.flatMap(it => it[1]);
    };

    fetchData()
      .then(res => setResult(res))
      .catch(err => {
        setResult([]);
        console.error("Complement static data error:", err);
      });
  }, [grpcClient, listComplementStaticData]);

  return result;
};

interface InventoryConfigurationProps {
  [key: string]: {
    value: string;
    label: string;
  }[];
}

export const useInventoryConfiguration = (): InventoryConfigurationProps => {
  const inventoryLines = useInventoryLines();
  const result: InventoryConfigurationProps = {
    accommodations: [],
    conditions: [],
    mealPlans: [],
    occupancies: [],
  };
  inventoryLines?.forEach(inventory => {
    if (
      inventory.accommodation &&
      !result.accommodations.find((it: any) => it.value === inventory.accommodation?.ticker)
    ) {
      result.accommodations.push({
        value: inventory.accommodation.ticker,
        label: inventory.accommodation.name,
      });
    }
    if (
      inventory.condition &&
      !result.conditions.find((it: any) => it.value === inventory.condition?.ticker)
    ) {
      result.conditions.push({
        value: inventory.condition.ticker,
        label: inventory.condition.name,
      });
    }
    if (
      inventory.mealPlan &&
      !result.mealPlans.find((it: any) => it.value === inventory.mealPlan?.ticker)
    ) {
      result.mealPlans.push({ value: inventory.mealPlan.ticker, label: inventory.mealPlan.name });
    }
    if (
      inventory.configuration &&
      !result.occupancies.find((it: any) => it.value === inventory.configuration?.ticker)
    ) {
      result.occupancies.push({
        value: inventory.configuration.ticker,
        label: inventory.configuration.name,
      });
    }
  });
  return result;
};
